import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { AlertStatus, IssueType, Severity } from '../../graphql/enums';
import { useFetcher } from '../../graphql/hooks/useFetcher';
import { LIST_ALERTS_MANAGEMENT } from '../../alerts/queries';

// ---------------------------------------------------------------------------
// CalendarEventData — the plain-object shape the calendar grids consume.
//
// This mirrors `CalendarEvent` from `@gripp/shared-ui/Calendar/calendar.types`
// exactly, but is defined here to avoid a shared-logic → shared-ui import
// cycle. The screen wiring casts the items to `CalendarEvent` directly — the
// shapes are identical.
// ---------------------------------------------------------------------------
export interface CalendarEventData {
  /**
   * For `activity` pills this is the issue id (clicking opens `/activity/:id`).
   * For `alert` pills this is the alert (workflow) id (clicking opens the
   * alerts-management screen).
   */
  id: string;
  /**
   * What this pill represents:
   * - `activity` — an activity/issue, placed at its `effectiveCreationDate`
   *   ("when it happened"). Renders in full type color once its day is today/past.
   *   A *fired* alert appears here too (firing creates an ALERT-type issue).
   * - `alert` — an upcoming alert from the alerts-management screen, placed at
   *   its `nextAlertDate` ("when it next fires"). A future firing renders in the
   *   scheduled (light-red) state — this is the "alerts as future activity" view.
   * The grids key on `id`+`kind` so activity and alert pills never collide.
   */
  kind: 'activity' | 'alert';
  /** Placement date: `effectiveCreationDate` (activity) or `nextAlertDate` (alert). */
  at: DateTime;
  /** Pill background — the activity's type color (`typeColors[variant]`). */
  color: string;
  /** Primary pill label (issue/alert title). */
  title: string;
  /** Optional second line (linked asset / target) when `showAssetInPill`. */
  asset?: string;
  /** Hour-of-day (0–23) for week-grid row placement. Derived from `at`. */
  hour: number;
}

// ---------------------------------------------------------------------------
// Color palette — copied from shared-ui's typeColors so we can resolve the
// color here without importing shared-ui.
// ---------------------------------------------------------------------------
const TYPE_COLORS: Record<string, string> = {
  attention: '#E24D2A',
  urgent: '#E24D2A',
  blocker: '#F9C431',
  pending: '#F9C431',
  done: '#36B37E',
  solved: '#36B37E',
  routines: '#4A90E2',
  note: '#7E4CC9',
  campaign: '#D649A6',
  notification: '#FF5505',
};

// Fallback when a severity doesn't map to a known variant.
const FALLBACK_COLOR = '#4A90E2';

// Base color for an alert pill. Alerts have no severity; their `nextAlertDate`
// is always in the future so they render in the scheduled (light-red) state,
// which overrides this fill — it only shows on the edge case of a firing that
// has already lapsed. Use the brand "notification" hue.
const ALERT_COLOR = TYPE_COLORS.notification;

// Severity → TypeVariant (mirrors severityToVariant in shared-ui).
const SEVERITY_TO_VARIANT: Record<Severity, string> = {
  [Severity.Red]: 'attention',
  [Severity.Yellow]: 'blocker',
  [Severity.Green]: 'done',
  [Severity.Purple]: 'note',
  [Severity.Blue]: 'routines',
};

// Issue-type overrides: campaign issues use the 'campaign' pink regardless of
// severity (mirrors the activity feed). `issue.type` is an `IssueType`, so we key
// on `IssueType.Campaign` ('CAMPAIGN') — NOT `ActivityType.CampaignCreated`
// ('CAMPAIGN_CREATED'), which never matches a findIssues `type` value.
const ISSUE_TYPE_VARIANT_OVERRIDES: Partial<Record<IssueType, string>> = {
  [IssueType.Campaign]: 'campaign',
};

// ---------------------------------------------------------------------------
// activityToCalendarEvent — pure mapping function (one place, easy to test).
// ---------------------------------------------------------------------------

/** Raw issue shape returned by the FIND_ISSUES_CALENDAR query. */
export interface CalendarIssue {
  id: string;
  title: string;
  severity: Severity;
  type: IssueType;
  effectiveCreationDate: string | null;
  createdAt: string;
  asset?: {
    name: string;
  } | null;
}

/**
 * Maps a raw issue from findIssues onto an `activity` CalendarEventData.
 *
 * - `at` / `hour`: derived from `effectiveCreationDate ?? createdAt` (luxon).
 * - `color`: `typeColors[activityTypeOverride ?? severityToVariant[severity]]`.
 *   CampaignCreated issues always get the campaign pink regardless of severity.
 * - `asset`: the asset's name if the issue has a linked asset.
 */
export function activityToCalendarEvent(issue: CalendarIssue): CalendarEventData {
  const dateStr = issue.effectiveCreationDate ?? issue.createdAt;
  const at: DateTime = DateTime.fromISO(dateStr);

  const typeVariant =
    ISSUE_TYPE_VARIANT_OVERRIDES[issue.type] ??
    SEVERITY_TO_VARIANT[issue.severity];

  const color = TYPE_COLORS[typeVariant] ?? FALLBACK_COLOR;

  return {
    id: issue.id,
    kind: 'activity',
    at,
    color,
    title: issue.title,
    asset: issue.asset?.name ?? undefined,
    hour: at.hour,
  };
}

// ---------------------------------------------------------------------------
// alertToCalendarEvent — maps an alerts-management alert onto a scheduled pill.
// ---------------------------------------------------------------------------

/** Raw alert shape returned by `workflow.listAlerts` (LIST_ALERTS_MANAGEMENT). */
export interface CalendarAlert {
  id: string;
  name?: string | null;
  routineName?: string | null;
  assetName?: string | null;
  groupName?: string | null;
  productName?: string | null;
  /** Number of assets this alert covers (1 for an asset alert, N for a group/product). */
  assetAlerts?: number | null;
  /** Next scheduled firing (computed live by the resolver); null when nothing upcoming. */
  nextAlertDate?: string | null;
  disabled?: boolean | null;
}

/**
 * Maps an upcoming alert onto an `alert` CalendarEventData placed at its
 * `nextAlertDate` — the next time it fires. This is the same data the
 * alerts-management screen shows. One pill per alert (a group/product alert that
 * covers many assets is a single pill; the target line notes the asset count).
 *
 * Returns `null` when the alert has no `nextAlertDate` (nothing upcoming) or is
 * disabled. Placement is always future, so it renders in the scheduled state.
 */
export function alertToCalendarEvent(
  alert: CalendarAlert
): CalendarEventData | null {
  if (alert.disabled || !alert.nextAlertDate) return null;
  const at: DateTime = DateTime.fromISO(alert.nextAlertDate);
  if (!at.isValid) return null;

  const title = alert.routineName || alert.name || 'Alert';

  // Second line: the target (asset / group / product); for a multi-asset alert,
  // note the count so a single pill still conveys its reach.
  const target =
    alert.assetName || alert.groupName || alert.productName || undefined;
  const count = alert.assetAlerts ?? 0;
  const asset =
    count > 1
      ? target
        ? `${target} · ${count} assets`
        : `${count} assets`
      : target;

  return {
    id: alert.id,
    kind: 'alert',
    at,
    color: ALERT_COLOR,
    title,
    asset,
    hour: at.hour,
  };
}

// ---------------------------------------------------------------------------
// useActivityCalendar hook
// ---------------------------------------------------------------------------

/**
 * Window (ISO string or luxon DateTime) passed to useActivityCalendar.
 * Using ISO strings avoids luxon import ceremony at call sites, but luxon
 * DateTime is accepted too (toISO() is called on .start / .end if present).
 */
export interface CalendarWindow {
  /** Start of the visible range (inclusive). ISO string or luxon DateTime. */
  start: DateTime | string;
  /** End of the visible range (inclusive). ISO string or luxon DateTime. */
  end: DateTime | string;
}

/**
 * Raw filter that mirrors the `FindIssuesFilter` GraphQL input. Callers
 * pass any subset of the filter the activity-list screen already uses;
 * `startDate` and `endDate` are injected from `window` and must not be
 * included in `filter` (they would be overwritten). `workspace.eq` is also
 * used to scope the alerts query (alerts-management is per-workspace).
 */
export interface CalendarFilter {
  severity?: Severity[];
  workspace?: { eq?: string };
  groupIds?: string[];
  includeUngrouped?: boolean;
  labelIds?: string[];
  includeUnlabeled?: boolean;
  keyword?: string;
  excludeActivityTypes?: IssueType[];
  [key: string]: unknown;
}

function toISO(value: DateTime | string): string {
  if (typeof value === 'string') return value;
  // DateTime is typed as `any` via the ambient shim in src/types/luxon.d.ts —
  // the cast is intentional and consistent with how the shim is documented.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (value as any).toISO() as string;
}

// listAlerts is date-windowed server-side, so a single visible month/week
// returns few alerts; one generous page covers them without pagination.
const ALERTS_PAGE_SIZE = 1000;

/**
 * Fetches everything the calendar plots for a visible window:
 *  - **Activities** — issues whose creation date falls in the window
 *    (`findIssues`), placed at `effectiveCreationDate` in full type color. A
 *    fired alert shows here (each firing creates an ALERT-type issue).
 *  - **Upcoming alerts** — the alerts-management alerts (`workflow.listAlerts`)
 *    whose `nextAlertDate` falls in the window, placed there as scheduled
 *    (light-red) pills — the "alerts as future activity" treatment. One pill per
 *    alert. Only fetched when a single workspace is selected (alerts-management
 *    is per-workspace); skipped for cross-workspace / aggregate views.
 *
 * The server caps the `findIssues` result at `MAX_UNBOUNDED_ITEMS`, so a busy
 * window can never exceed MongoDB's 16MB BSON limit; `count` is the true total
 * so `isTruncated` can warn the user.
 *
 * **Alerts are a separate, non-blocking, date-windowed query.** `listAlerts`
 * takes `startDate`/`endDate` (on the computed `nextAlertDate`) and applies them
 * BEFORE its enrichment lookups, so a month/week query only touches the alerts
 * firing in range (a workspace-wide fetch was tens of seconds; a windowed one is
 * a few). It still runs independently of the (fast) activities query so the
 * calendar renders activities immediately and alert pills appear when ready.
 *
 * @param filter - Activity filter (severity, groups, labels, keyword, etc.).
 *   Should NOT include `startDate` / `endDate` — those come from `window`.
 *   `workspace.eq` also scopes the alerts query.
 * @param window - Visible calendar range `{ start, end }` (luxon or ISO).
 * @param options - Optional react-query passthrough. `enabled` (default `true`)
 *   lets a caller skip the fetch while the calendar isn't shown or the workspace
 *   id hasn't resolved yet.
 */
export function useActivityCalendar(
  filter: CalendarFilter,
  window: CalendarWindow,
  options?: { enabled?: boolean }
) {
  const startISO = toISO(window.start);
  const endISO = toISO(window.end);

  const issueVariables = {
    filter: {
      ...filter,
      startDate: startISO,
      endDate: endISO,
    },
  };

  const workspaceId = filter.workspace?.eq;
  const enabled = options?.enabled ?? true;

  const issueFetcher = useFetcher<
    { issue: { findIssues: { count: number; items: CalendarIssue[] } } },
    typeof issueVariables
  >(FIND_ISSUES_CALENDAR);

  const alertsFetcher = useFetcher<
    { workflow: { listAlerts: { count: number; items: CalendarAlert[] } } },
    {
      filter: {
        workspaceId: string;
        status: AlertStatus;
        startDate: string;
        endDate: string;
      };
      page: { limit: number; offset: number };
    }
  >(LIST_ALERTS_MANAGEMENT);

  // Activities — fast, per-window. Drives the calendar's loading spinner.
  const issuesQuery = useQuery({
    queryKey: ['issue', 'calendar', startISO, endISO, filter] as const,
    queryFn: () => issueFetcher(issueVariables),
    enabled,
    select: (data) => {
      const findIssues = data?.issue?.findIssues;
      const activityEvents = (findIssues?.items ?? []).map(
        activityToCalendarEvent
      );
      const activityCount = findIssues?.count ?? activityEvents.length;
      return {
        events: activityEvents,
        activityCount,
        isTruncated: activityCount > activityEvents.length,
      };
    },
  });

  // Upcoming alerts — date-windowed server-side (fast) and non-blocking: the
  // calendar shows activities while this resolves. Skipped for cross-workspace /
  // aggregate views (alerts-management is per-workspace).
  const alertsQuery = useQuery({
    queryKey: ['calendar', 'alerts', workspaceId, startISO, endISO] as const,
    queryFn: () =>
      alertsFetcher({
        filter: {
          workspaceId: workspaceId as string,
          status: AlertStatus.Enabled,
          startDate: startISO,
          endDate: endISO,
        },
        page: { limit: ALERTS_PAGE_SIZE, offset: 0 },
      }),
    enabled: enabled && !!workspaceId,
    select: (data) =>
      (data?.workflow?.listAlerts?.items ?? [])
        .map(alertToCalendarEvent)
        .filter((e): e is CalendarEventData => e !== null),
  });

  const activityEvents = issuesQuery.data?.events ?? [];
  const alertEvents = alertsQuery.data ?? [];

  // Both sources are already windowed server-side; just merge for a stable ref.
  const events = useMemo(
    () => [...activityEvents, ...alertEvents],
    [activityEvents, alertEvents]
  );

  const activityCount =
    issuesQuery.data?.activityCount ?? activityEvents.length;
  const windowedAlertCount = events.length - activityEvents.length;

  return {
    /** Mapped calendar events (activity pills + upcoming-alert pills). */
    events,
    /** True total plotted in the window (activities + upcoming alerts). */
    totalCount: activityCount + windowedAlertCount,
    /** True when the server capped the activities window (more than shown). */
    isTruncated: issuesQuery.data?.isTruncated ?? false,
    /**
     * Drives the calendar spinner off the activities query only, so the calendar
     * renders immediately and never blocks on the alerts query. `alertsLoading`
     * is exposed separately for callers that want to indicate alert pills are
     * still arriving.
     */
    isFetching: issuesQuery.isFetching,
    isLoading: issuesQuery.isLoading,
    alertsLoading: alertsQuery.isFetching,
  };
}

// ---------------------------------------------------------------------------
// GraphQL query — mirrors the fields already used in activityList.tsx plus
// the fields activityToCalendarEvent needs. No pagination variables since the
// hook fetches the whole visible window at once.
// ---------------------------------------------------------------------------
const FIND_ISSUES_CALENDAR = `
  query FindIssuesCalendar($filter: FindIssuesFilter) {
    issue {
      findIssues(filter: $filter) {
        count
        items {
          id
          title
          severity
          type
          effectiveCreationDate
          createdAt
          asset {
            name
          }
        }
      }
    }
  }
`;
